import { Box, Group } from '@mantine/core';
import dayjs from 'dayjs';
import type { MinifiedTask } from '../../../../../api/tasks-client/task-client.type';
import i18n from '../../../../../base-dictionary';
import { downloadCSV } from '../../../../../helpers/csv-download';
import { formatDate, toLocalDate } from '../../../../../helpers/date-time-helpers';
import { capitalizeFirstLetter } from '../../../../../helpers/string-helpers/string-helpers';
import { secondaryBase } from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../../ui-library/typography/typography';
import type { TableColumn } from '../../../../data-management/components/table';
import { getLatestStatus, getTaskStatusColor } from '../related-tasks-tab.helpers';

/**
 * Returns an array of column configurations for the tasks table
 *
 * @returns {TableColumn<MinifiedTask>[]} Array of column configurations
 */
const getTableColumns = (): TableColumn<MinifiedTask>[] => [
  {
    key: 'id',
    label: i18n.t('strategy.initiative_performance.related_tasks.table.id'),
    width: '20%',
    sortable: true,
    render: (_, task) => (
      <Group
        gap={8}
        wrap="nowrap"
        style={{ cursor: 'pointer', minWidth: 0 }}
        onClick={() => {
          if (task.url) {
            window.open(task.url, '_blank', 'noopener, noreferrer');
          }
        }}
      >
        <Tooltip label={getLatestStatus(task)} variant="white">
          <Box
            style={{
              borderRadius: '50%',
              width: 14,
              height: 14,
              backgroundColor: getTaskStatusColor(getLatestStatus(task)),
              flexShrink: 0,
            }}
          />
        </Tooltip>
        <Text
          size="small"
          color={secondaryBase}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {task.title}
        </Text>
        <Tooltip variant="white" label={i18n.t('common.visit_source_page')} position="right">
          <Icon name="arrow_outward" size={16} color={secondaryBase} style={{ flexShrink: 0 }} pointer />
        </Tooltip>
      </Group>
    ),
  },
  {
    key: 'name',
    label: i18n.t('strategy.initiative_performance.related_tasks.table.name'),
    width: '40%',
    sortable: true,
    render: (_, task) => task.name,
  },
  {
    key: 'epic',
    label: i18n.t('strategy.initiative_performance.related_tasks.table.epic'),
    width: '10%',
    sortable: true,
    render: (_, task) => (
      <Box
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {task.epic ? task.epic.title : '-'}
      </Box>
    ),
  },
  {
    key: 'points',
    label: i18n.t('strategy.initiative_performance.related_tasks.table.points'),
    width: '10%',
    sortable: true,
    render: (_, task) => task.points || '-',
  },
  {
    key: 'type',
    label: i18n.t('strategy.initiative_performance.related_tasks.table.type'),
    width: '10%',
    sortable: true,
    render: (_, task) => (task.type ? capitalizeFirstLetter(task.type) : '-'),
  },
  {
    key: 'created_date',
    label: i18n.t('strategy.initiative_performance.related_tasks.table.created'),
    width: '10%',
    sortable: true,
    render: (_, task) => formatDate(task.created_date),
  },
];

/**
 * Custom sort function for task records
 *
 * @param {MinifiedTask[]} data - The array of task records to sort
 * @param {keyof MinifiedTask} sortBy - The field to sort by
 * @param {'asc' | 'desc'} direction - The sort direction (ascending or descending)
 * @returns {MinifiedTask[]} A new sorted array of task records
 */
const customSort = (data: MinifiedTask[], sortBy: keyof MinifiedTask, direction: 'asc' | 'desc'): MinifiedTask[] => {
  return [...data].sort((a, b) => {
    const multiplier = direction === 'asc' ? 1 : -1;

    switch (sortBy) {
      case 'id':
        return multiplier * String(a.title || '').localeCompare(String(b.title || ''));

      case 'name':
        return multiplier * String(a.name || '').localeCompare(String(b.name || ''));

      case 'epic': {
        const aEpicTitle = a.epic?.title || '';
        const bEpicTitle = b.epic?.title || '';
        return multiplier * aEpicTitle.localeCompare(bEpicTitle);
      }

      case 'points': {
        const aPoints = a.points || 0;
        const bPoints = b.points || 0;
        return multiplier * (aPoints - bPoints);
      }

      case 'type': {
        const aType = a.type || '';
        const bType = b.type || '';
        return multiplier * aType.localeCompare(bType);
      }

      case 'created_date': {
        const aDate = a.created_date ? toLocalDate(a.created_date) : dayjs(0);
        const bDate = b.created_date ? toLocalDate(b.created_date) : dayjs(0);
        return multiplier * aDate.diff(bDate);
      }

      default:
        return 0;
    }
  });
};

/**
 * Handles downloading a CSV file for the tasks table
 *
 * @param {MinifiedTask[]} tasks - The tasks to download
 * @returns {void}
 */
const handleTasksDownloadCSV = (tasks: MinifiedTask[]): void => {
  try {
    const headers = ['ID', 'Name', 'Epic', 'Points', 'Type', 'Status', 'Created Date', 'URL'];

    const rows = tasks.map((task) => [
      task.title || 'N/A',
      task.name || 'N/A',
      task.epic ? task.epic.title : 'N/A',
      task.points || 0,
      task.type ? capitalizeFirstLetter(task.type) : 'N/A',
      getLatestStatus(task) || 'N/A',
      task.created_date ? toLocalDate(task.created_date).format('MMM D, YYYY') : 'N/A',
      task.url || 'N/A',
    ]);

    downloadCSV([headers, ...rows], 'related-tasks.csv');
  } catch (error) {
    console.error('Error downloading CSV', error);
  }
};

export { customSort, getTableColumns, handleTasksDownloadCSV };
